import React, { useEffect, useState } from 'react';
import { Modal, Descriptions } from 'antd';
import { getLocalDateString } from '../utils/date';
import { apiFetch, apiUrl } from '../api';
import { getEmployeeTranslatedLabel } from '../utils/companyRoles';

const EmployeeDetailsModal = ({ open, onCancel, employee }) => {
  const [formFields, setFormFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (open && formFields.length === 0) {
      setLoadingFields(true);
      const fetchFields = async () => {
        try {
          const response = await apiFetch('/api/employee-fields');
          const data = await response.json();
          if (response.ok && data.data?.fields) {
             setFormFields(data.data.fields.filter(f => f.enabled).sort((a,b) => (a.order || 0) - (b.order || 0)));
          }
        } catch (e) {
          console.error('Failed to load employee fields', e);
        } finally {
          setLoadingFields(false);
        }
      };
      fetchFields();
    }
  }, [open, formFields.length]);

  if (!employee) return null;

  const customFields = employee.customFields || {};

  const getFieldValue = (key) => {
    if (['firstName', 'lastName', 'email', 'department', 'phoneNumber', 'address', 'employeeId', 'role', 'status'].includes(key)) {
      return employee[key];
    }
    
    return customFields[key] !== undefined ? customFields[key] : employee[key];
  };

  return (
    <Modal
      title="Employee Details"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <div className="flex flex-row gap-4 mb-4 mt-2">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-slate-800 m-0">{employee.firstName} {employee.lastName}</h2>
            <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded capitalize ${
              employee.status === 'inactive'
                ? 'bg-rose-50 text-rose-600 border border-rose-200'
                : 'bg-emerald-50 text-emerald-600 border border-emerald-200'
            }`}>
              {employee.status === 'inactive' ? 'Inactive' : 'Active'}
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-1 mt-0.5">{employee.department || 'No Department'}</p>
          <div className="inline-block bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[11px] font-mono font-medium border border-slate-200">
            {employee.employeeId || 'N/A'}
          </div>
        </div>
      </div>

      <Descriptions 
        title={<span className="text-sm font-semibold text-slate-700">Profile Details</span>} 
        bordered 
        column={2} 
        size="small" 
        className="mb-4"
        labelStyle={{ padding: '6px 10px', fontSize: '12px', width: '130px', backgroundColor: '#f8fafc' }}
        contentStyle={{ padding: '6px 10px', fontSize: '12px' }}
      >
        {loadingFields ? (
           <Descriptions.Item label="Loading..." span={2}>
             <div className="flex justify-center p-2">
               <div className="animate-spin w-4 h-4 border-2 border-slate-300 border-t-blue-500 rounded-full" />
             </div>
           </Descriptions.Item>
        ) : (
           formFields.map(field => {
             const val = getFieldValue(field.key);
             let displayVal = Array.isArray(val) ? val.join(', ') : val;
             if (displayVal && (field.type === 'date' || field.type === 'datetime')) {
                displayVal = getLocalDateString(displayVal);
             }
             return (
               <Descriptions.Item key={field.key} label={getEmployeeTranslatedLabel(field.label, user?.companyName)}>
                 {displayVal || 'N/A'}
               </Descriptions.Item>
             );
           })
        )}
      </Descriptions>
      
      <Descriptions 
        title={<span className="text-sm font-semibold text-slate-700">Account Information</span>} 
        bordered 
        column={2} 
        size="small"
        labelStyle={{ padding: '6px 10px', fontSize: '12px', width: '130px', backgroundColor: '#f8fafc' }}
        contentStyle={{ padding: '6px 10px', fontSize: '12px' }}
      >
        <Descriptions.Item label="Role" className="capitalize">{employee.role || 'Employee'}</Descriptions.Item>
        <Descriptions.Item label="Status" className="capitalize">{employee.status || 'Active'}</Descriptions.Item>
      </Descriptions>
    </Modal>
  );
};

export default EmployeeDetailsModal;
